import { useMutation } from '@apollo/client';
import { useState } from 'react';

import { cacheUtils, useCompleteFragment } from '@eluve/apollo-client';
import { SimpleTooltip } from '@eluve/components';
import {
  Button,
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  Icon,
  Text,
  useToast,
} from '@eluve/components';
import { graphql } from '@eluve/graphql.tada';

import { getAdminTenantsSummaryFilterQuery } from './query';

interface DeleteTenantActionProps {
  tenantId: string;
}

const deleteTenantMutation = graphql(`
  mutation deleteTenant($tenantId: uuid!, $deletedAt: timestamptz!) {
    updateAdminTenants(
      where: { id: { _eq: $tenantId } }
      _set: { deletedAt: $deletedAt }
    ) {
      returning {
        __typename
        id
        deletedAt
      }
    }
  }
`);

const adminTenantUsersCount = graphql(`
  fragment UserCount on AdminTenants {
    __typename
    id
    name
    userCount
  }
`);

export const DeleteTenantAction: React.FC<DeleteTenantActionProps> = ({
  tenantId,
}) => {
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [deleteTenant, { loading }] = useMutation(deleteTenantMutation, {
    refetchQueries: [getAdminTenantsSummaryFilterQuery],
  });
  const toast = useToast();

  const tenant = useCompleteFragment({
    fragment: adminTenantUsersCount,
    key: { id: tenantId },
  });

  const tenantUsers = tenant?.userCount ?? 0;

  const handleDeleteTenant = async () => {
    deleteTenant({
      variables: {
        tenantId,
        deletedAt: new Date().toISOString(),
      },
      onCompleted: () => {
        setIsDialogOpen(false);
      },
      onError: () => {
        toast.error('Failed to delete organization');
      },
      update: (cache) => {
        cacheUtils.evict({
          typeName: 'Tenants',
          key: { id: tenantId },
        });
        cache.gc();
      },
    });
  };

  if (tenantUsers === undefined || tenantUsers > 0) {
    return null;
  }

  return (
    <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen} modal>
      <DialogTrigger asChild>
        <Button variant="destructive" aria-label="Delete organization">
          <SimpleTooltip label="Delete Organization">
            <Icon name="Trash" size="xl" />
          </SimpleTooltip>
        </Button>
      </DialogTrigger>
      <DialogContent size="lg">
        <DialogHeader>
          <DialogTitle>Delete Organization</DialogTitle>
        </DialogHeader>
        <Text size="md">
          Are you sure you want to soft delete{' '}
          <span className="font-bold text-text-negative">{tenant?.name}</span>?
        </Text>
        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => setIsDialogOpen(false)}
            disabled={loading}
          >
            Cancel
          </Button>
          <Button
            onClick={handleDeleteTenant}
            variant="destructive"
            disabled={loading}
          >
            Delete
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
